import React, { useCallback, useEffect, useState } from 'react';
import { Icon, channelLabel } from './inboxIcons';
import ReportsPage from './ReportsPage';

// Analytics: Overview ("needs attention" + tiles), Order journey (problems at the step where they
// happen), and Team & speed (the previous Reports page).
const CH_COLOR = { wa: 'var(--wa)', email: 'var(--em)', fb: 'var(--fb)', form: 'var(--form)', ig: 'var(--ig)', chat: 'var(--chat)', manual: 'var(--chat)' };
const CH_ORDER = ['wa', 'email', 'fb', 'ig', 'form', 'chat', 'manual'];
const TOPIC = {
  product_or_offer_question: 'Product / offer', wholesale: 'Wholesale', payment_issue: 'Payment', cancel: 'Cancel',
  order_status: 'Order status', delay_complaint: 'Delay', not_received: 'Not received',
  damaged_or_wrong: 'Damaged / wrong', colour_mismatch: 'Colour', refund: 'Refund', invoice: 'Invoice',
};
const WA_LABEL = {
  order_confirmed: 'Order confirmed', cod_confirm: 'COD confirmation', order_delay: 'Delay update', shipped: 'Shipped',
  out_for_delivery: 'Out for delivery', delivered: 'Delivered + review', ndr_rescue: 'Failed delivery', cart_recovery: 'Cart reminders',
};
const REASON = { 'ok:colour': 'Colour differs', 'ok:quality': 'Fabric quality', 'ok:late': 'Arrived late', 'bad:colour': 'Colour differs', 'bad:quality': 'Fabric quality', 'bad:damaged': 'Damaged', 'bad:late': 'Arrived late' };

const pctTxt = (v, d = 0) => (v == null ? '—' : `${(v * 100).toFixed(d)}%`);
const MONEY_LABEL = { cart_recovery: 'Cart reminders', weekly_drop: 'Weekly new prints', win_back: 'Win-back', diwali: 'Diwali campaign', complete_look: 'Complete the look', outfit_ask: 'Show us your outfit', care_tips: 'Care tips' };
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const lakh = (n) => (n >= 100000 ? `₹${(n / 100000).toFixed(1)} L` : inr(n));
function dur(min) {
  if (min == null) return '—';
  if (min < 60) return `${Math.round(min)} min`;
  if (min < 48 * 60) return `${Math.round((min / 60) * 10) / 10} h`;
  return `${Math.round((min / 1440) * 10) / 10} days`;
}
function Delta({ now, prev, lowerBetter, asPts }) {
  if (now == null || prev == null || prev === 0) return null;
  const diff = asPts ? (now - prev) * 100 : ((now - prev) / prev) * 100;
  if (Math.abs(diff) < 1) return <span className="cx-delta flat">no change</span>;
  const good = lowerBetter ? diff < 0 : diff > 0;
  return <span className={`cx-delta ${good ? 'up' : 'down'}`}>{diff > 0 ? '+' : '−'}{Math.round(Math.abs(diff))}{asPts ? ' pts' : '%'}</span>;
}

function Bars({ rows, max, suffix = '' }) {
  const m = max || Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="cx-an-bars">
      {rows.map((r) => (
        <div key={r.label} className="cx-an-bar">
          <span>{r.label}</span>
          <span className="cx-an-track"><i style={{ width: `${Math.max(r.value ? 2 : 0, (r.value / m) * 100)}%`, background: r.color || 'var(--acc)' }} /></span>
          <b className="cx-num">{r.value}{suffix}</b>
        </div>
      ))}
    </div>
  );
}

function StackedDays({ perDay }) {
  const totals = perDay.map((d) => CH_ORDER.reduce((s, c) => s + (d[c] || 0), 0));
  const max = Math.max(1, ...totals);
  const label = (k) => new Date(`${k}T00:00:00+05:30`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return (
    <>
      <div className="cx-an-stack" role="img" aria-label={`Conversations per day, highest ${max}`}>
        {perDay.map((d, i) => (
          <div key={d.day} title={`${label(d.day)}: ${totals[i]}`}>
            {CH_ORDER.filter((c) => d[c]).map((c) => <i key={c} style={{ height: `${(d[c] / max) * 100}%`, background: CH_COLOR[c] }} />)}
          </div>
        ))}
      </div>
      <div className="cx-vol-axis"><span>{label(perDay[0].day)}</span><span>{label(perDay[perDay.length - 1].day)}</span></div>
    </>
  );
}

function Hours({ hours }) {
  const max = Math.max(1, ...hours);
  return (
    <>
      <div className="cx-an-hours" role="img" aria-label="Conversations by hour of day">
        {hours.map((n, h) => <div key={h} title={`${h}:00 · ${n}`}><i className={h >= 20 || h < 9 ? 'night' : ''} style={{ height: `${(n / max) * 100}%` }} /></div>)}
      </div>
      <div className="cx-vol-axis"><span>12 am</span><span>6 am</span><span>12 pm</span><span>6 pm</span><span>11 pm</span></div>
    </>
  );
}

function Overview({ a, onGo, onTab }) {
  const o = a.overview;
  const channels = Object.entries(o.byChannel || {}).sort((x, y) => y[1] - x[1]);
  const goTo = (link) => (link === 'journey' ? onTab('journey') : onGo?.(link));
  const waRows = Object.entries(o.whatsapp.byAutomation || {});
  return (
    <>
      <div className="cx-metrics">
        <div className="cx-metric"><span>Conversations</span><b>{o.conversations.now}</b><small><Delta now={o.conversations.now} prev={o.conversations.prev} lowerBetter /> started in period</small></div>
        <div className="cx-metric"><span>Orders that needed help</span><b>{pctTxt(o.contactRate.now, 1)}</b><small>{o.conversations.now} conversations · {o.orders.now} orders</small></div>
        <div className="cx-metric">
          <span>First reply (median)</span><b>{dur(o.firstReplyMin.now)}</b>
          <small>{o.firstReplyMin.now != null && o.firstReplyMin.now > o.firstReplyGoalMin ? <span className="cx-chip cx-c-waiting">over {o.firstReplyGoalMin} min goal</span> : `goal ${o.firstReplyGoalMin} min`} · AI draft {dur(o.aiReplyMin)}</small>
        </div>
        <div className="cx-metric"><span>Dispatched late</span><b>{pctTxt(o.lateDispatchRate)}</b><small>past the promised dispatch date</small></div>
        <div className="cx-metric"><span>Returned to us (RTO)</span><b>{pctTxt(o.rto.rate, 1)}</b><small>{o.rto.count} orders</small></div>
      </div>

      <div className="cx-an-main">
        <div className="cx-an-col">
          <div className="cx-two even">
            <section className="cx-panel">
              <div className="cx-panel-h"><h2>Why customers write</h2><span>AI-sorted topics</span></div>
              <div className="cx-panel-b">
                {o.topics.length ? <Bars rows={o.topics.slice(0, 7).map((t) => ({ label: t.label, value: t.count }))} /> : <span className="cx-hint">Topics appear once the AI agent has read new messages.</span>}
                {o.untagged > 0 && <p className="cx-hint" style={{ marginTop: 10 }}>{o.untagged} without a topic (a greeting or a photo only).</p>}
              </div>
            </section>
            <section className="cx-panel">
              <div className="cx-panel-h"><h2>AI agent</h2><span>{o.drafts.decided} drafts decided</span></div>
              <div className="cx-panel-b">
                <Bars rows={[{ label: 'Sent as written', value: o.drafts.asIs, color: 'var(--done-fg)' }, { label: 'Edited first', value: o.drafts.edited, color: 'var(--wait-fg)' }, { label: 'Rejected', value: o.drafts.rejected, color: 'var(--new-fg)' }]} max={Math.max(1, o.drafts.decided)} />
                <p className="cx-hint" style={{ marginTop: 10 }}>{pctTxt(o.drafts.asIsPct)} sent as written <Delta now={o.drafts.asIsPct} prev={o.drafts.asIsPctPrev} asPts /></p>
              </div>
            </section>
          </div>

          <section className="cx-panel">
            <div className="cx-panel-h"><h2>Conversations per day</h2>
              <span className="cx-an-legend">{channels.map(([c]) => <span key={c}><i style={{ background: CH_COLOR[c] || 'var(--em)' }} />{channelLabel(c)}</span>)}</span>
            </div>
            <div className="cx-panel-b"><StackedDays perDay={o.perDay} /></div>
          </section>

          <div className="cx-two even">
            <section className="cx-panel">
              <div className="cx-panel-h"><h2>When customers write</h2><span>India time</span></div>
              <div className="cx-panel-b">
                <Hours hours={o.hours} />
                <p className="cx-hint" style={{ marginTop: 10 }}>{o.night} of {o.conversations.now} started between 8 pm and 5 am (shown in amber).</p>
              </div>
            </section>
            <section className="cx-panel">
              <div className="cx-panel-h"><h2>Sales recovery</h2><span>abandoned carts</span></div>
              <div className="cx-panel-b">
                {o.carts ? (
                  <div className="cx-an-mini">
                    <div><span>Carts left</span><b className="cx-num">{o.carts.total}</b><small>{lakh(o.carts.value)} in carts</small></div>
                    <div><span>Recovered</span><b className="cx-num">{o.carts.converted}</b><small>{inr(o.carts.convertedValue)} · {pctTxt(o.carts.total ? o.carts.converted / o.carts.total : null)}</small></div>
                    <div><span>WhatsApp reminders</span><b className="cx-num">{o.cartReminders.sent}</b><small>{o.cartReminders.sent ? `${o.cartReminders.read} read` : 'none sent yet'}</small></div>
                  </div>
                ) : <span className="cx-hint">Needs access to abandoned carts.</span>}
              </div>
            </section>
          </div>

          <section className="cx-panel">
            <div className="cx-panel-h"><h2>WhatsApp updates</h2><span>{o.whatsapp.paused ? 'paused' : `${o.whatsapp.total.sent} sent`}</span></div>
            {waRows.length === 0 ? (
              <div className="cx-empty" style={{ padding: 24 }}><strong>No updates sent yet</strong>Read rates, failures and COD replies appear here once automations are switched on.</div>
            ) : (
              <table className="cx-table">
                <thead><tr><th>Update</th><th className="r">Sent</th><th className="r">Delivered</th><th className="r">Read</th><th className="r">Failed</th></tr></thead>
                <tbody>{waRows.map(([k, s]) => <tr key={k}><td>{WA_LABEL[k] || k}</td><td className="r cx-num">{s.sent}</td><td className="r cx-num">{s.delivered}</td><td className="r cx-num">{s.read}</td><td className="r cx-num">{s.failed}</td></tr>)}</tbody>
              </table>
            )}
            {o.whatsapp.readMedianMin != null && <p className="cx-hint" style={{ padding: '0 16px 14px' }}>Read a median of {dur(o.whatsapp.readMedianMin)} after sending.</p>}
          </section>
        </div>

        <aside className="cx-panel cx-an-att" aria-labelledby="cx-att-h">
          <div className="cx-panel-h"><h2 id="cx-att-h">Needs attention</h2><span>{o.attention.filter((x) => x.level !== 'good').length} to look at</span></div>
          <div className="cx-an-att-list">
            {o.attention.length === 0 && <p className="cx-hint" style={{ padding: 16 }}>Nothing needs attention.</p>}
            {o.attention.map((x) => (
              <div key={x.title} className={`cx-an-att-item ${x.level}`}>
                <i aria-hidden="true" />
                <div>
                  <b>{x.title}</b>
                  <p>{x.text}</p>
                  {x.link && <button className="cx-linkbtn" onClick={() => goTo(x.link)}>{x.link === 'journey' ? 'See order journey' : x.link === 'whatsapp' ? 'Open WhatsApp' : 'Open settings'}</button>}
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </>
  );
}

function Asked({ asked }) {
  const list = Object.entries(asked || {}).sort((x, y) => y[1] - x[1]);
  if (!list.length) return <div className="cx-an-asked muted">No questions about this step</div>;
  return <div className="cx-an-asked">{list.map(([k, n]) => <span key={k} className="cx-chip cx-c-plain">{TOPIC[k] || k} {n}</span>)}</div>;
}

function WaLine({ wa, keys }) {
  const s = keys.map((k) => wa[k]).filter(Boolean).reduce((a, x) => ({ sent: a.sent + x.sent, read: a.read + x.read, failed: a.failed + x.failed }), { sent: 0, read: 0, failed: 0 });
  if (!s.sent && !s.failed) return <div className="cx-an-wa muted"><Icon name="whatsapp" size={13} />No WhatsApp sent yet</div>;
  return <div className="cx-an-wa"><Icon name="whatsapp" size={13} />{s.sent} sent · {s.read} read{s.failed ? ` · ${s.failed} failed` : ''}</div>;
}

function Journey({ a }) {
  const s = a.journey.steps;
  const wa = a.journey.whatsapp || {};
  const rto = a.journey.rto;
  const fb = s.delivered.feedback;
  const fbTotal = fb.good + fb.ok + fb.bad;
  const disp = s.printing.onTime + s.printing.late + s.printing.lateWaiting;
  const reasons = Object.entries(s.delivered.reasons).sort((x, y) => y[1] - x[1]).slice(0, 3);
  const stages = [
    { n: 1, title: 'Placed', big: s.placed.orders, rows: [['COD', s.placed.cod], ['Prepaid', s.placed.prepaid], ['Cancelled', s.placed.cancelled]], asked: s.placed.asked, wa: ['order_confirmed'] },
    { n: 2, title: 'COD confirm', big: s.cod.asked || s.placed.cod, sub: s.cod.asked ? 'asked on WhatsApp' : 'COD orders', rows: [['Confirmed', s.cod.confirmed], ['Change address', s.cod.addressChange], ['Asked to cancel', s.cod.cancelAsked], ['No reply, call', s.cod.noReplyCall, s.cod.noReplyCall ? 'warn' : '']], wa: ['cod_confirm'] },
    { n: 3, title: 'Printing', big: s.printing.reached, sub: 'started printing', rows: [['Dispatched on time', s.printing.onTime], ['Dispatched late', s.printing.late, s.printing.late ? 'warn' : ''], ['Late, not dispatched', s.printing.lateWaiting, s.printing.lateWaiting ? 'bad' : '']], asked: s.printing.asked, wa: ['order_delay'] },
    { n: 4, title: 'Shipped', big: s.shipped.shipped, rows: [['Failed delivery', s.shipped.failedDelivery, s.shipped.failedDelivery ? 'warn' : ''], ['Reattempts booked', `${s.shipped.reattemptsConfirmed}/${s.shipped.reattempts}`], ['Returned (RTO)', s.shipped.rto, s.shipped.rto ? 'warn' : '']], asked: s.shipped.asked, wa: ['shipped', 'out_for_delivery', 'ndr_rescue'] },
    { n: 5, title: 'Delivered', big: s.delivered.delivered, rows: fbTotal ? [['Loved it', pctTxt(fb.good / fbTotal)], ["It's okay", pctTxt(fb.ok / fbTotal)], ['Not happy', pctTxt(fb.bad / fbTotal), fb.bad ? 'warn' : '']] : [['Ratings', 'none yet']], wa: ['delivered'], foot: reasons.length ? `Top reasons: ${reasons.map(([k, v]) => `${REASON[k] || k.split(':')[1]} ${v}`).join(', ')}` : null },
    { n: 6, title: 'After delivery', big: null, rows: [['Google review links', s.after.reviewLinks], ['Photo credits given', s.after.credits]], asked: s.after.asked },
  ];
  return (
    <>
      <p className="cx-lede" style={{ margin: '0 0 14px' }}>Each column is a step of the order: how many orders reached it, what went wrong there, what customers asked about it, and what WhatsApp sent. Orders placed in the period.</p>
      <div className="cx-an-journey">
        {stages.map((st) => (
          <section key={st.n} className="cx-an-stage" aria-label={st.title}>
            <div className="cx-an-stage-h"><span>{st.n}</span><b>{st.title}</b></div>
            {st.big != null && <div className="cx-an-big cx-num">{st.big}<small>{st.sub || 'orders'}</small></div>}
            <dl>{st.rows.map(([k, v, lvl]) => <React.Fragment key={k}><dt>{k}</dt><dd className={`cx-num ${lvl || ''}`}>{v}</dd></React.Fragment>)}</dl>
            {st.foot && <p className="cx-hint">{st.foot}</p>}
            {st.asked && <Asked asked={st.asked} />}
            {st.wa && <WaLine wa={wa} keys={st.wa} />}
          </section>
        ))}
      </div>

      <div className="cx-two even" style={{ marginTop: 20 }}>
        <section className="cx-panel">
          <div className="cx-panel-h"><h2>Dispatch against promise</h2><span>{disp} orders past their dispatch date or dispatched</span></div>
          <div className="cx-panel-b">
            <Bars rows={[{ label: 'On time', value: s.printing.onTime, color: 'var(--done-fg)' }, { label: 'Late', value: s.printing.late, color: 'var(--wait-fg)' }, { label: 'Late, not dispatched yet', value: s.printing.lateWaiting, color: 'var(--new-fg)' }]} />
            <p className="cx-hint" style={{ marginTop: 10 }}>Promise = the dispatch window shown to customers for the slowest fabric in the order (working days, Mon–Sat).</p>
          </div>
        </section>
        <section className="cx-panel">
          <div className="cx-panel-h"><h2>Returned to us (RTO)</h2><span>{rto.total} orders</span></div>
          <div className="cx-panel-b">
            {rto.total === 0 ? <span className="cx-hint">No returns in this period.</span> : (
              <>
                <Bars rows={[{ label: 'COD', value: rto.cod, color: 'var(--wait-fg)' }, { label: 'Prepaid', value: rto.prepaid }]} max={rto.total} />
                <div className="cx-an-split">
                  <div><h3>Courier</h3>{rto.couriers.map((c) => <div key={c.key}><span>{c.key}</span><b className="cx-num">{c.count}</b></div>)}</div>
                  <div><h3>Top pincodes</h3>{rto.pincodes.map((c) => <div key={c.key}><span className="cx-num">{c.key}</span><b className="cx-num">{c.count}</b></div>)}</div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

// ---- Customer actions: cancel / return / address / tracking / talk to us (from the action log) ----
const STAGE_LABEL = { printing: 'Printing', packing: 'Packing', on_hold: 'On hold', shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled', returning_to_us: 'Coming back (RTO)', returned_to_us: 'Returned (RTO)', unknown: 'Unknown' };
const rowsOf = (list, labels) => (list || []).map((r) => ({ label: labels ? labels[r.label] || r.label : r.label, value: r.value }));
function OutcomeLine({ b }) {
  if (!b) return null;
  return <p className="cx-hint" style={{ marginTop: 10 }}>{b.decided} closed{b.open ? ` · ${b.open} still open` : ''}{b.medianHours != null ? ` · typically resolved in ${dur(b.medianHours * 60)}` : ''}</p>;
}
function ActionPanel({ title, sub, children }) {
  return <section className="cx-panel"><div className="cx-panel-h"><h2>{title}</h2>{sub && <span>{sub}</span>}</div><div className="cx-panel-b">{children}</div></section>;
}
function Empty({ children }) { return <span className="cx-hint">{children}</span>; }

function CustomerActions({ x }) {
  const t = x.totals;
  return (
    <>
      <div className="cx-metrics">
        <div className="cx-metric"><span>Cancel requests</span><b>{t.cancel_request.now}</b><small><Delta now={t.cancel_request.now} prev={t.cancel_request.prev} lowerBetter /> {x.cancel.kept ? `${x.cancel.kept} kept the order` : ''}</small></div>
        <div className="cx-metric"><span>Return requests</span><b>{t.return_request.now}</b><small><Delta now={t.return_request.now} prev={t.return_request.prev} lowerBetter /> {x.returns.lateRequests ? `${x.returns.lateRequests} after 3 days` : ''}</small></div>
        <div className="cx-metric"><span>Tracked it themselves</span><b>{t.track.now}</b><small>order status from the WhatsApp button, no team needed</small></div>
        <div className="cx-metric"><span>Handled by buttons</span><b>{t.self_served?.now ?? 0}</b><small>{x.selfServed && (x.selfServed.abandonedCancel || x.selfServed.abandonedReturn) ? `${x.selfServed.abandonedCancel} cancel and ${x.selfServed.abandonedReturn} return menus left unfinished` : 'chats that needed no one from the team'}</small></div>
        <div className="cx-metric"><span>Address changes</span><b>{t.address_change.now}</b><small>asked before dispatch</small></div>
        <div className="cx-metric"><span>After-hours · busy notes</span><b>{t.after_hours.now} · {t.busy_note.now}</b><small>{x.busy.medianWaitMin != null ? `busy note after ~${x.busy.medianWaitMin} min wait` : 'automatic replies'}</small></div>
      </div>
      {x.history > 0 && <p className="cx-hint" style={{ margin: '0 0 12px' }}>Includes {x.history} past returns and cancellations loaded from Zoho and v9 orders. Their reasons were never recorded, so they show as "Not recorded". New requests from the WhatsApp buttons carry a reason.</p>}

      <div className="cx-two even">
        <ActionPanel title="Why customers cancel" sub={`${x.cancel.count} requests`}>
          {x.cancel.count ? <Bars rows={rowsOf(x.cancel.reasons)} /> : <Empty>No cancel requests in this period.</Empty>}
          <OutcomeLine b={x.cancel} />
        </ActionPanel>
        <ActionPanel title="When they cancel" sub="stage at the request">
          {x.cancel.stages.length ? <Bars rows={rowsOf(x.cancel.stages, STAGE_LABEL)} /> : <Empty>Shown for new requests (past ones only know today's stage).</Empty>}
          {x.cancel.payment.length > 0 && <p className="cx-hint" style={{ marginTop: 10 }}>{x.cancel.payment.map((p) => `${p.label} ${p.value}`).join(' · ')}{x.cancel.late ? ` · ${x.cancel.late} were already late` : ''}{x.cancel.medianDaysSinceOrder != null ? ` · typically ${x.cancel.medianDaysSinceOrder} days after ordering` : ''}</p>}
        </ActionPanel>
      </div>

      <div className="cx-two even">
        <ActionPanel title="Why customers return" sub={`${x.returns.count} requests`}>
          {x.returns.count ? <Bars rows={rowsOf(x.returns.reasons)} /> : <Empty>No return requests in this period.</Empty>}
          {x.returns.daysAfterDelivery.length > 0 && <p className="cx-hint" style={{ marginTop: 10 }}>Asked {x.returns.daysAfterDelivery.map((d) => `${d.label}: ${d.value}`).join(' · ')} after delivery</p>}
          <OutcomeLine b={x.returns} />
        </ActionPanel>
        <ActionPanel title="How returns ended" sub="picked by the team when closing">
          {x.returns.outcomes.length ? <Bars rows={rowsOf(x.returns.outcomes)} /> : <Empty>Outcomes appear as the team closes return tasks.</Empty>}
        </ActionPanel>
      </div>

      <div className="cx-two even">
        <ActionPanel title="Returned fabrics" sub="by quality and fabric">
          {x.returns.byQuality.length ? <Bars rows={rowsOf(x.returns.byQuality)} /> : <Empty>No returns.</Empty>}
          {x.returns.byFabric.length > 0 && <div style={{ marginTop: 12 }}><Bars rows={rowsOf(x.returns.byFabric)} /></div>}
        </ActionPanel>
        <ActionPanel title="Production batches with returns" sub="returned items per print batch">
          {x.returns.byBatch.length ? <Bars rows={rowsOf(x.returns.byBatch)} /> : <Empty>No batch information on returned items yet.</Empty>}
          <p className="cx-hint" style={{ marginTop: 10 }}>A batch high on this list is worth checking with the print house.</p>
        </ActionPanel>
      </div>

      <div className="cx-two even">
        <ActionPanel title="Order tracking" sub={`${x.tracking.count} checks`}>
          {x.tracking.count ? <Bars rows={rowsOf(x.tracking.stages, STAGE_LABEL)} /> : <Empty>No tracking checks yet.</Empty>}
          {x.tracking.late > 0 && <p className="cx-hint" style={{ marginTop: 10 }}>{x.tracking.late} of them were for late orders.</p>}
        </ActionPanel>
        <ActionPanel title="Talk to us" sub={`${x.talk.count} requests`}>
          {x.talk.count ? <Bars rows={rowsOf(x.talk.topics)} /> : <Empty>No requests yet.</Empty>}
          <OutcomeLine b={x.talk} />
        </ActionPanel>
      </div>
      {x.customers.withData > 0 && <p className="cx-hint">{x.customers.repeat} of {x.customers.withData} actions came from repeat customers.</p>}
    </>
  );
}

function ActionsTab({ api, days }) {
  const [x, setX] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    setErr(null);
    api(`/api/v1/support/analytics/actions?days=${days}`).then((r) => alive && setX(r.actions)).catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api, days]);
  if (err) return <div className="cx-empty"><strong>Couldn't load customer actions</strong>{err}</div>;
  if (!x) return <div aria-busy="true"><div className="cx-skel" style={{ height: 96, margin: 0 }} /><div className="cx-skel" style={{ height: 260, margin: '20px 0 0' }} /></div>;
  return <CustomerActions x={x} />;
}

// ---- Weekly report (built every Monday 10 AM, see backend cxWeekly.js) ----
const LEVEL = { high: ['High', 'cx-lv-high'], watch: ['Watch', 'cx-lv-watch'], good: ['Good', 'cx-lv-good'] };
const weeklyParam = () => { try { return new URLSearchParams(window.location.search).get('weekly'); } catch { return null; } };

function WeekTrend({ rows }) {
  if (!rows?.length) return null;
  const W = 560, H = 120, L = 32, B = 22, max = Math.max(5, ...rows.map((r) => r.chats));
  const x = (i) => L + (i * (W - L - 12)) / Math.max(1, rows.length - 1);
  const y = (v) => 8 + (H - B - 8) * (1 - v / max);
  const line = (k) => rows.map((r, i) => `${x(i)},${y(r[k])}`).join(' ');
  const label = (id) => new Date(`${id}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`Chats per week: ${rows.map((r) => r.chats).join(', ')}`}>
        {[0, max].map((v) => <g key={v}><line x1={L} x2={W - 8} y1={y(v)} y2={y(v)} stroke="var(--line)" /><text x={L - 6} y={y(v) + 4} textAnchor="end" fontSize="10" fill="var(--ink-3)">{v}</text></g>)}
        <polyline points={line('chats')} fill="none" stroke="var(--acc)" strokeWidth="2.5" />
        <polyline points={line('selfServed')} fill="none" stroke="#c27a2c" strokeWidth="2.5" />
        {rows.map((r, i) => <text key={r.week} x={x(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--ink-3)">{label(r.week)}</text>)}
      </svg>
      <p className="cx-hint" style={{ margin: '6px 0 0' }}><b style={{ color: 'var(--acc)' }}>━</b> Chats · <b style={{ color: '#c27a2c' }}>━</b> Solved by buttons</p>
    </>
  );
}

// Campaign results: every WhatsApp campaign's cost, orders, order value and ROAS against the owner's target (waRevenue.js)
const VERDICT = { keep: ['Keep', 'cx-c-resolved'], watch: ['Watch', 'cx-c-waiting'], stop: ['Stop or change', 'cx-c-new'], service: ['Service', 'cx-c-plain'], none: ['Not sent', 'cx-c-plain'] };
function CampaignsTab({ api, days }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState({});
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => { setErr(null); api(`/api/v1/support/whatsapp/revenue?days=${days}`).then((r) => { setD(r); setTarget(String(r.targetRoas)); }).catch((e) => setErr(e.message)); }, [api, days]);
  useEffect(() => { load(); }, [load]);
  const saveTarget = async () => {
    setSaving(true);
    try { await api('/api/v1/support/whatsapp/revenue/target', 'POST', { targetRoas: Number(target) }); load(); } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };
  if (err && !d) return <div className="cx-empty"><strong>Couldn't load campaign results</strong>{err}</div>;
  if (!d) return <div className="cx-skel" style={{ height: 260 }} />;
  const rows = d.rows.filter((r) => r.sent || r.orders).sort((a, b) => (b.roas ?? -1) - (a.roas ?? -1));
  const t = d.total;
  return (
    <>
      <ActionPanel title="Campaign results" sub={`last ${days} days · ${t.sent} delivered · cost ${inr(t.cost)} · ${t.orders} orders · ${inr(t.ordered)} · ROAS ${t.roas ?? '–'}x (target ${d.targetRoas}x)`}>
        <div className="cx-an-tablebox">
          <table className="cx-an-table">
            <thead><tr><th>Campaign</th><th className="r">Delivered</th><th className="r">Cost</th><th className="r">Orders</th><th className="r">Order value</th><th className="r">ROAS</th><th className="r">Confirmed</th><th>Vs. 10% not sent</th><th>Verdict</th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const v = VERDICT[r.verdict?.code] || VERDICT.none;
                const h = r.holdout;
                return (
                  <React.Fragment key={r.key}>
                    <tr onClick={() => r.runs && setOpen((o) => ({ ...o, [r.key]: !o[r.key] }))} style={r.runs ? { cursor: 'pointer' } : undefined} aria-expanded={r.runs ? !!open[r.key] : undefined}>
                      <td>{MONEY_LABEL[r.key] || r.key}{r.runs && <span className="cx-hint"> · {r.runs.length} sends {open[r.key] ? '▴' : '▾'}</span>}</td>
                      <td className="r cx-num">{r.sent}{r.notDelivered > 0 && <span className="cx-hint"> (+{r.notDelivered} stopped)</span>}</td>
                      <td className="r cx-num">{inr(r.cost)}</td>
                      <td className="r cx-num">{r.orders}</td>
                      <td className="r cx-num">{inr(r.ordered)}</td>
                      <td className="r cx-num"><b>{r.roas != null ? `${r.roas}x` : '–'}</b></td>
                      <td className="r cx-num">{r.roasConfirmed != null ? `${r.roasConfirmed}x` : '–'}</td>
                      <td>{!h ? <span className="cx-hint">no test group</span> : !h.ready ? <span className="cx-hint">collecting</span> : h.significant ? `${h.extraOrders >= 0 ? '+' : ''}${h.extraOrders} orders${h.trueRoas != null ? ` · true ${h.trueRoas}x` : ''}` : <span className="cx-hint">no clear difference yet</span>}</td>
                      <td><span className={`cx-chip ${v[1]}`} title={r.verdict?.label}>{v[0]}</span></td>
                    </tr>
                    {open[r.key] && r.runs.map((u) => (
                      <tr key={`${r.key}-${u.run}`} className="cx-an-sub">
                        <td style={{ paddingLeft: 24 }}>{u.run}</td><td className="r cx-num">{u.sent}</td><td className="r cx-num">{inr(u.cost)}</td><td className="r cx-num">{u.orders}</td><td className="r cx-num">{inr(u.ordered)}</td><td className="r cx-num">{u.roas != null ? `${u.roas}x` : '–'}</td><td /><td /><td />
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="cx-hint" style={{ marginTop: 10 }}>ROAS = order value ÷ cost. Confirmed counts delivered orders only (COD can still be refused). An order counts for the latest offer the customer got within its window (3 days cart, 7 days offers, 14 days after-sale). Verdict: Keep at or above target with 5+ orders; Watch with fewer orders or near target; Stop or change clearly below target, or when the 10% test group ordered just as much.</p>
        <div className="wa-feature" style={{ marginTop: 6 }}>
          <span><b>Target ROAS</b><small>The line for the verdicts. Below it a campaign costs more than it earns for you.</small></span>
          <span style={{ display: 'flex', gap: 8, flex: '0 0 auto', alignItems: 'center' }}>
            <input className="cx-input" type="number" min="0.5" step="0.5" value={target} onChange={(e) => setTarget(e.target.value)} style={{ width: 90 }} aria-label="Target ROAS" />
            <button className="cx-btn" onClick={saveTarget} disabled={saving || Number(target) === d.targetRoas}>{saving ? 'Saving…' : 'Save'}</button>
          </span>
        </div>
        {err && <div className="cx-err">{err}</div>}
      </ActionPanel>
    </>
  );
}

function WeeklyTab({ api }) {
  const [week, setWeek] = useState(weeklyParam);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    setErr(null);
    api(`/api/v1/support/analytics/weekly${week ? `?week=${encodeURIComponent(week)}` : ''}`).then((r) => alive && setD(r)).catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api, week]);
  if (err) return <div className="cx-empty"><strong>Couldn't load the weekly report</strong>{err}</div>;
  if (!d) return <div aria-busy="true"><div className="cx-skel" style={{ height: 96, margin: 0 }} /><div className="cx-skel" style={{ height: 260, margin: '20px 0 0' }} /></div>;
  const r = d.report;
  if (!r) return <div className="cx-empty"><strong>No report for this week</strong>Reports are made every Monday at 10 AM for the week before.</div>;
  const a = r.actions;
  const t = a.totals;
  const flows = [
    ['Track my order', t.track.now, 'Saw the order status'],
    ['Cancel order', r.requests?.cancel_request.now ?? 0, a.cancel.outcomes[0] ? `${a.cancel.outcomes[0].label} (${a.cancel.outcomes[0].value})` : '—'],
    ['Return / exchange', r.requests?.return_request.now ?? 0, a.returns.outcomes[0] ? `${a.returns.outcomes[0].label} (${a.returns.outcomes[0].value})` : '—'],
    ['Talk to us', t.talk_to_team.now, a.talk.topics[0] ? `Top topic: ${a.talk.topics[0].label}` : '—'],
    ['Late order checks', t.late_order?.now ?? 0, a.lateOrders?.outcomes[0] ? `${a.lateOrders.outcomes[0].label} (${a.lateOrders.outcomes[0].value})` : 'Each gets a "send an update" task'],
    ['Change address', t.address_change.now, a.address.outcomes[0] ? `${a.address.outcomes[0].label} (${a.address.outcomes[0].value})` : '—'],
    ['Auto-replies', t.after_hours.now + t.busy_note.now, `${t.busy_note.now} busy notes · ${t.after_hours.now} after hours`],
  ];
  const fr = r.firstReply;
  return (
    <>
      <div className="cx-an-weekbar">
        <span className="cx-select"><select value={r.weekId} onChange={(e) => setWeek(e.target.value)} aria-label="Week">
          {d.weeks.map((w) => <option key={w.weekId} value={w.weekId}>{w.label}{w.partial ? ' (so far)' : ''}</option>)}
        </select></span>
        <span className="cx-hint">Made {new Date(r.builtAtMs).toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}{r.partial ? ' · week not finished yet' : ''}{r.sent?.results?.some((x) => x.sent) ? ' · summary sent on WhatsApp' : ''}</span>
      </div>

      <div className="cx-metrics">
        <div className="cx-metric"><span>Chats</span><b>{r.chats}</b><small>{r.prev.chats >= 20 ? <><Delta now={r.chats} prev={r.prev.chats} /> vs last week</> : `last week ${r.prev.chats}`}</small></div>
        <div className="cx-metric"><span>Solved by buttons</span><b>{a.selfServed.count}</b><small>{t.track.now} order-tracking checks</small></div>
        <div className="cx-metric"><span>First reply (median)</span><b>{fr.medianMin == null ? '—' : dur(fr.medianMin)}</b><small>{fr.n ? `${fr.withinHourPct}% within an hour · working hours` : 'not enough replies to measure'}</small></div>
        <div className="cx-metric"><span>Waiting for a reply now</span><b>{r.needsReply.count}</b><small>{r.needsReply.count ? `oldest ${r.needsReply.oldestDays ? `${r.needsReply.oldestDays} day${r.needsReply.oldestDays === 1 ? '' : 's'}` : 'under a day'}` : 'inbox clear'} · when the report was made</small></div>
      </div>

      <ActionPanel title="Needs attention" sub={`${r.insights.filter((i) => i.level !== 'good').length} to look at`}>
        {r.insights.length ? (
          <ul className="cx-an-insights">
            {r.insights.map((i) => (
              <li key={i.title}><span className={`cx-lv ${LEVEL[i.level][1]}`}>{LEVEL[i.level][0]}</span><div><b>{i.title}</b><span className="cx-hint">{i.detail}</span></div></li>
            ))}
          </ul>
        ) : <Empty>Nothing stood out this week.</Empty>}
      </ActionPanel>

      <div className="cx-two even">
        <ActionPanel title="Why customers cancel" sub={`${r.requests?.cancel_request.now ?? 0} requests on WhatsApp`}>
          {a.cancel.count ? <Bars rows={rowsOf(a.cancel.reasons)} /> : <Empty>No cancellations this week.</Empty>}
          {a.history > 0 && <p className="cx-hint" style={{ marginTop: 10 }}>Includes orders cancelled in the system without a customer request (reason "Not recorded").</p>}
        </ActionPanel>
        <ActionPanel title="Why customers return" sub={`${r.requests?.return_request.now ?? 0} requests on WhatsApp`}>
          {a.returns.count ? <Bars rows={rowsOf(a.returns.reasons)} /> : <Empty>No returns this week.</Empty>}
        </ActionPanel>
      </div>

      <div className="cx-two even">
        <ActionPanel title="Where chats came from" sub={`${r.chats} chats with a customer message`}>
          {r.byChannel.length ? <Bars rows={r.byChannel.map((c) => ({ label: c.label, value: c.value }))} /> : <Empty>No customer messages.</Empty>}
        </ActionPanel>
        <ActionPanel title="Most returned fabrics" sub="from return requests">
          {r.returnedProducts.length ? <Bars rows={r.returnedProducts.map((p) => ({ label: p.topReason ? `${p.label} · ${p.topReason}` : p.label, value: p.value }))} /> : <Empty>No returns with fabric details this week.</Empty>}
        </ActionPanel>
      </div>

      <ActionPanel title="Button flows" sub="what customers tapped and how it ended">
        <div className="cx-an-tablebox">
          <table className="cx-an-table">
            <thead><tr><th>Flow</th><th className="r">This week</th><th>Most common result</th></tr></thead>
            <tbody>{flows.map(([f, n, res]) => <tr key={f}><td>{f}</td><td className="r cx-num">{n}</td><td>{res}</td></tr>)}</tbody>
          </table>
        </div>
      </ActionPanel>

      {r.money && r.money.total.sent > 0 && (
        <ActionPanel title="Money from WhatsApp" sub={`${r.money.total.sent} sales messages · cost ${inr(r.money.total.cost)} · ${r.money.total.orders} orders after a message · ${inr(r.money.total.delivered)} delivered${r.money.total.perRupee != null ? ` · ₹${r.money.total.perRupee} back per ₹1` : ''}`}>
          <div className="cx-an-tablebox">
            <table className="cx-an-table">
              <thead><tr><th>Automation</th><th className="r">Delivered</th><th className="r">Cost</th><th className="r">Orders</th><th className="r">Ordered</th><th className="r">Delivered</th><th className="r">₹ per ₹1</th><th>Vs. 10% not sent</th></tr></thead>
              <tbody>{r.money.rows.filter((x) => x.sent || x.orders).map((x) => (
                <tr key={x.key}>
                  <td>{MONEY_LABEL[x.key] || x.key}<span className="cx-hint"> · {x.windowDays} d</span></td>
                  <td className="r cx-num">{x.sent}{x.notDelivered > 0 && <span className="cx-hint"> (+{x.notDelivered} stopped)</span>}</td><td className="r cx-num">{inr(x.cost)}</td><td className="r cx-num">{x.orders}</td>
                  <td className="r cx-num">{inr(x.ordered)}</td><td className="r cx-num">{inr(x.delivered)}</td><td className="r cx-num">{x.perRupee ?? '–'}</td>
                  <td>{!x.holdout ? 'no test group' : x.holdout.ready ? `${Math.round(x.holdout.sentRate * 1000) / 10}% vs ${Math.round(x.holdout.heldRate * 1000) / 10}% ordered (${x.holdout.extraOrders >= 0 ? '+' : ''}${x.holdout.extraOrders} orders)` : `collecting (${x.holdout.sentPeople}/${x.holdout.heldPeople} of 30)`}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <p className="cx-hint" style={{ marginTop: 10 }}>An order counts for the latest sales message the customer got within its window (3 d cart, 7 d offers, 14 d after-sale). Delivered excludes cancelled and refused orders. Some would have ordered anyway: the "not sent" comparison shows what the message really added.</p>
        </ActionPanel>
      )}

      {r.drop && (
        <ActionPanel title="Weekly new prints" sub={`${r.drop.sent} delivered${r.drop.stopped ? ` (+${r.drop.stopped} stopped by Meta)` : ''} · ${r.drop.ordered} ordered within 7 days · ₹${r.drop.revenue.toLocaleString('en-IN')}`}>
          <div className="cx-an-tablebox">
            <table className="cx-an-table">
              <thead><tr><th>Sent</th><th className="r">Messages</th><th className="r">Read</th><th className="r">Tapped a print</th><th className="r">Ordered (7 days)</th><th className="r">Revenue</th></tr></thead>
              <tbody>{r.drop.slots.map((x) => <tr key={x.slot}><td>{x.label}</td><td className="r cx-num">{x.sent}</td><td className="r cx-num">{x.readPct}%</td><td className="r cx-num">{x.clicked || 0}</td><td className="r cx-num">{x.ordered}</td><td className="r cx-num">₹{x.revenue.toLocaleString('en-IN')}</td></tr>)}</tbody>
            </table>
          </div>
          {r.drop.topPrints?.length > 0 && <p className="cx-hint" style={{ marginTop: 10 }}><b>Most tapped prints:</b> {r.drop.topPrints.map((p) => `${p.label} (${p.value})`).join(' · ')}</p>}
          <p className="cx-hint" style={{ marginTop: 10 }}>Orders count when the same customer orders within 7 days of the message. After 4 weeks, keep the time that brings more orders.</p>
        </ActionPanel>
      )}

      {r.reads && r.reads.read > 0 && (
        <ActionPanel title="When customers read WhatsApp" sub={`last 30 days · ${r.reads.read} of ${r.reads.sent} messages read · half within ${r.reads.medianMinToRead} min`}>
          <div className="cx-hours" role="img" aria-label="Reads by hour of day">
            {r.reads.byHour.map((n, h) => {
              const max = Math.max(1, ...r.reads.byHour);
              return <span key={h} title={`${h}:00 · ${n} reads`}><i style={{ height: `${Math.max(n ? 6 : 0, (n / max) * 100)}%` }} /><small>{h % 3 === 0 ? (h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`) : ''}</small></span>;
            })}
          </div>
          <p className="cx-hint" style={{ marginTop: 10 }}>
            {r.reads.withinHourPct}% are read within an hour.
            {r.reads.bestSend?.length ? ` Messages sent at ${r.reads.bestSend.map((b) => `${b.hour % 12 || 12} ${b.hour < 12 ? 'AM' : 'PM'} (${b.pct}% read in 1 h)`).join(', ')} got read fastest.` : ''}
            {' '}Busiest reading days: {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => [d, r.reads.byDay[i]]).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([d]) => d).join(', ')}.
          </p>
        </ActionPanel>
      )}

      {r.colour && (
        <ActionPanel title="Colour complaints" sub={`"Colour not as in photo" returns, last 90 days · ${r.colour.questionsThisWeek} colour questions before buying this week`}>
          {r.colour.prints.length ? <Bars rows={r.colour.prints.map((p) => ({ label: p.label, value: p.value }))} /> : <Empty>No colour complaints in the last 90 days.</Empty>}
          <p className="cx-hint" style={{ marginTop: 10 }}>Retake the photos of prints at the top; film these first when daylight videos start.</p>
        </ActionPanel>
      )}

      {r.carts && (
        <ActionPanel title="Abandoned cart follow-ups" sub={`${r.carts.followUps} follow-ups · ${r.carts.converted} carts ordered`}>
          <div className="cx-metrics" style={{ marginBottom: 10 }}>
            <div className="cx-metric"><span>Calls</span><b>{r.carts.calls}</b><small>{r.carts.calls ? `${r.carts.reached} reached` : 'none logged'}</small></div>
            <div className="cx-metric"><span>Ordered after follow-up</span><b>{r.carts.convertedAfterFollowUp}</b><small>of {r.carts.converted} carts that ordered</small></div>
            <div className="cx-metric"><span>Value ordered</span><b>{`₹${r.carts.convertedValue.toLocaleString('en-IN')}`}</b><small>cart value of converted carts</small></div>
          </div>
          {r.carts.results.length ? <Bars rows={r.carts.results} /> : <Empty>No follow-up results logged this week.</Empty>}
        </ActionPanel>
      )}

      {r.problems && (
        <ActionPanel title="Problems the team reported" sub={`${r.problems.count} this week · ${r.problems.closed} closed`}>
          {r.problems.byType.length ? <Bars rows={r.problems.byType} /> : <Empty>Nothing reported this week.</Empty>}
          {r.problems.noAnswer.length > 0 && <p className="cx-hint" style={{ marginTop: 10 }}><b>Questions with no good answer yet:</b> {r.problems.noAnswer.join(' · ')}</p>}
          {r.problems.waitingOpen > 0 && <p className="cx-hint" style={{ marginTop: 6, color: '#a3302a' }}>{r.problems.waitingOpen} with a customer waiting still open.</p>}
        </ActionPanel>
      )}

      <ActionPanel title="Last 6 weeks" sub="WhatsApp joined Support on 11 Oct, so earlier weeks are mostly email">
        <WeekTrend rows={r.trend} />
      </ActionPanel>
    </>
  );
}

function AnalyticsPage({ api, onGo }) {
  const [tab, setTab] = useState(() => { if (weeklyParam()) return 'weekly'; try { return localStorage.getItem('cx-an-tab') || 'overview'; } catch { return 'overview'; } });
  const [days, setDays] = useState(30);
  const [a, setA] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const pick = (t) => { setTab(t); try { localStorage.setItem('cx-an-tab', t); } catch { /* storage unavailable */ } };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api(`/api/v1/support/analytics?days=${days}`);
      setA(r.analytics);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, days]);
  const own = tab === 'team' || tab === 'actions' || tab === 'weekly' || tab === 'campaigns'; // tabs that load their own data
  useEffect(() => { if (!own) load(); }, [load, own]);

  return (
    <div className="cx-page">
      <div className="cx-page-in cx-an">
        <div className="cx-page-head">
          <div>
            <h1>Analytics</h1>
            <p className="cx-lede">{tab === 'weekly' ? 'One week at a time, made every Monday at 10 AM and summarised on WhatsApp.' : `What customers need, where orders go wrong, and how fast we help. Last ${days} days, compared with the ${days} before.`}</p>
          </div>
          <div className="cx-page-actions">
            {tab !== 'weekly' && <div className="cx-seg" role="group" aria-label="Period" style={{ minWidth: 240 }}>
              {[7, 30, 90].map((d) => <button key={d} aria-pressed={days === d} onClick={() => setDays(d)}>{d} days</button>)}
            </div>}
            {!own && <button className="cx-btn cx-btn-icon" onClick={load} disabled={loading} aria-label="Refresh"><Icon name="refresh" size={16} /></button>}
          </div>
        </div>
        <div className="wa-tabs" role="tablist" aria-label="Analytics sections">
          {[['overview', 'Overview'], ['campaigns', 'Campaign results'], ['weekly', 'Weekly report'], ['journey', 'Order journey'], ['actions', 'Customer actions'], ['team', 'Team & speed']].map(([id, l]) => (
            <button key={id} role="tab" aria-selected={tab === id} onClick={() => pick(id)}>{l}</button>
          ))}
        </div>

        {tab === 'campaigns' ? <CampaignsTab api={api} days={days} /> : tab === 'team' ? <ReportsPage api={api} embedded days={days} /> : tab === 'actions' ? <ActionsTab api={api} days={days} /> : tab === 'weekly' ? <WeeklyTab api={api} /> : error && !a ? (
          <div className="cx-empty"><strong>Couldn't load analytics</strong>{error}<div style={{ marginTop: 12 }}><button className="cx-btn" onClick={load}>Try again</button></div></div>
        ) : !a ? (
          <div aria-busy="true"><div className="cx-skel" style={{ height: 96, margin: 0 }} /><div className="cx-skel" style={{ height: 260, margin: '20px 0 0' }} /></div>
        ) : (
          <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
            {tab === 'overview' ? <Overview a={a} onGo={onGo} onTab={pick} /> : <Journey a={a} />}
          </div>
        )}
      </div>
    </div>
  );
}

export default AnalyticsPage;
